/**
 * DrugInputField - Input component for j_code array field
 *
 * Manages a list of drug input items with add/remove functionality.
 * Each item includes code, name, strength, frequency, formulation, and indication.
 *
 * Exports:
 * - DrugInputField: Main input component
 * - DrugInputFieldPreview: Preview component for table cells
 */

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@components/ui/accordion';
import { Button } from '@components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@components/ui/dialog';
import { Input } from '@components/ui/input';
import { Plus, Trash2 } from 'lucide-react';
import React, { useState } from 'react';

import { SchemaProvider, useSchemaContextSafe } from './context/SchemaContext';
import { PillPreviewList, type PreviewPill } from './PillPreviewList';
import type { InputArrayProps, PreviewProps } from './types';

// Schema path prefix used for the drug input array's subfields. The schema's
// top-level field id for this array is `j_code` (see fields/index.ts registry),
// so subfields appear in the schema as e.g. "j_code.code" / "j_code.name".
const DRUG_PATH_PREFIX = 'j_code';

/** Renders the red `*` indicator when the schema marks the field required. */
const RequiredAsterisk: React.FC<{ required: boolean }> = ({ required }) =>
  required ? <span className="text-destructive ml-1">*</span> : null;

// =============================================================================
// TYPES
// =============================================================================

/**
 * Drug input item shape (defined locally, schema provides field definitions at runtime)
 */
interface DrugItem {
  code: string;
  name: string;
  strength?: string;
  frequency?: string;
  formulation?: string;
  indication?: string;
}

// =============================================================================
// MAIN INPUT COMPONENT
// =============================================================================

/**
 * Create an empty drug input item
 */
const createEmptyItem = (): DrugItem => ({
  code: '',
  name: '',
});

/**
 * Public component — mounts SchemaProvider so the inner body can consume
 * `isFieldRequired` from context. The inner body MUST be a separate component;
 * calling useSchemaContextSafe() in the same component that renders
 * <SchemaProvider> would see the outer (missing) context, not the one it
 * just installed.
 */
export const DrugInputField: React.FC<InputArrayProps> = props => (
  <SchemaProvider>
    <DrugInputFieldInner {...props} />
  </SchemaProvider>
);

const DrugInputFieldInner: React.FC<InputArrayProps> = ({
  data,
  onChange,
  errors = {},
  disabled = false,
}) => {
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [deleteConfirmIndex, setDeleteConfirmIndex] = useState<number | null>(null);

  // Schema-driven required indicators — same rationale as SiteOfCareInputField.
  const { isFieldRequired } = useSchemaContextSafe();
  const fieldRequired = (subfieldId: string) => isFieldRequired(subfieldId, DRUG_PATH_PREFIX);

  // Cast data to expected shape (schema provides field definitions at runtime)
  const items = (Array.isArray(data) ? data : []) as DrugItem[];

  const handleAddItem = () => {
    const newItem = createEmptyItem();
    const newItems = [...items, newItem];
    onChange(newItems);
    // Auto-expand the newly added item
    setExpandedItems(prev => [...prev, `item-${items.length}`]);
  };

  const handleDeleteItem = (index: number) => {
    const newItems = items.filter((_, i) => i !== index);
    onChange(newItems);
    setDeleteConfirmIndex(null);
  };

  const handleItemChange = (index: number, field: keyof DrugItem, value: string) => {
    const newItems = [...items];
    newItems[index] = { ...newItems[index], [field]: value };
    onChange(newItems);
  };

  const getItemLabel = (item: DrugItem, index: number): string => {
    if (item.code) return item.code;
    if (item.name) return item.name;
    return `Drug ${index + 1}`;
  };

  if (items.length === 0) {
    return (
      <div className="py-2">
        <div className="text-sm text-muted-foreground italic mb-2">No drugs added</div>
        {!disabled && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddItem}
            className="h-7 text-xs"
          >
            <Plus className="w-3 h-3 mr-1" />
            Add Drug
          </Button>
        )}
      </div>
    );
  }

  return (
    <div>
      <Accordion
        type="multiple"
        value={expandedItems}
        onValueChange={setExpandedItems}
        className="space-y-1"
      >
        {items.map((item, index) => {
          const itemKey = `item-${index}`;
          const itemLabel = getItemLabel(item, index);

          return (
            <AccordionItem key={itemKey} value={itemKey} className="border rounded-md bg-muted/30">
              <AccordionTrigger className="px-3 py-2 text-sm font-medium hover:bg-muted/50 rounded-md data-[state=open]:rounded-b-none">
                <div className="flex items-center gap-2 flex-1">
                  <span>{itemLabel}</span>
                </div>
                {!disabled && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={e => {
                      e.stopPropagation();
                      setDeleteConfirmIndex(index);
                    }}
                    className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive ml-2"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                )}
              </AccordionTrigger>
              <AccordionContent className="px-3 pb-3">
                <div className="space-y-3">
                  {/* Code */}
                  <div>
                    <label
                      htmlFor={`drug-${index}-code`}
                      className="text-xs text-muted-foreground mb-1 block"
                    >
                      J-Code
                      <RequiredAsterisk required={fieldRequired('code')} />
                    </label>
                    <Input
                      id={`drug-${index}-code`}
                      type="text"
                      value={item.code || ''}
                      onChange={e => handleItemChange(index, 'code', e.target.value)}
                      placeholder="e.g., J1745"
                      disabled={disabled}
                      className={`h-8 text-sm ${errors[`${index}.code`] ? 'border-destructive' : ''}`}
                    />
                    {errors[`${index}.code`] && (
                      <div className="text-xs text-destructive mt-1">{errors[`${index}.code`]}</div>
                    )}
                  </div>

                  {/* Name */}
                  <div>
                    <label
                      htmlFor={`drug-${index}-name`}
                      className="text-xs text-muted-foreground mb-1 block"
                    >
                      Drug Name
                      <RequiredAsterisk required={fieldRequired('name')} />
                    </label>
                    <Input
                      id={`drug-${index}-name`}
                      type="text"
                      value={item.name || ''}
                      onChange={e => handleItemChange(index, 'name', e.target.value)}
                      placeholder="e.g., Infliximab"
                      disabled={disabled}
                      className={`h-8 text-sm ${errors[`${index}.name`] ? 'border-destructive' : ''}`}
                    />
                    {errors[`${index}.name`] && (
                      <div className="text-xs text-destructive mt-1">{errors[`${index}.name`]}</div>
                    )}
                  </div>

                  {/* Strength */}
                  <div>
                    <label
                      htmlFor={`drug-${index}-strength`}
                      className="text-xs text-muted-foreground mb-1 block"
                    >
                      Strength
                    </label>
                    <Input
                      id={`drug-${index}-strength`}
                      type="text"
                      value={item.strength || ''}
                      onChange={e => handleItemChange(index, 'strength', e.target.value)}
                      placeholder="e.g., 100mg"
                      disabled={disabled}
                      className="h-8 text-sm"
                    />
                  </div>

                  {/* Frequency */}
                  <div>
                    <label
                      htmlFor={`drug-${index}-frequency`}
                      className="text-xs text-muted-foreground mb-1 block"
                    >
                      Frequency
                    </label>
                    <Input
                      id={`drug-${index}-frequency`}
                      type="text"
                      value={item.frequency || ''}
                      onChange={e => handleItemChange(index, 'frequency', e.target.value)}
                      placeholder="e.g., Every 8 weeks"
                      disabled={disabled}
                      className="h-8 text-sm"
                    />
                  </div>

                  {/* Formulation */}
                  <div>
                    <label
                      htmlFor={`drug-${index}-formulation`}
                      className="text-xs text-muted-foreground mb-1 block"
                    >
                      Formulation
                    </label>
                    <Input
                      id={`drug-${index}-formulation`}
                      type="text"
                      value={item.formulation || ''}
                      onChange={e => handleItemChange(index, 'formulation', e.target.value)}
                      placeholder="e.g., IV infusion"
                      disabled={disabled}
                      className="h-8 text-sm"
                    />
                  </div>

                  {/* Indication */}
                  <div>
                    <label
                      htmlFor={`drug-${index}-indication`}
                      className="text-xs text-muted-foreground mb-1 block"
                    >
                      Indication
                    </label>
                    <Input
                      id={`drug-${index}-indication`}
                      type="text"
                      value={item.indication || ''}
                      onChange={e => handleItemChange(index, 'indication', e.target.value)}
                      placeholder="e.g., Rheumatoid Arthritis"
                      disabled={disabled}
                      className="h-8 text-sm"
                    />
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>

      {/* Add button */}
      {!disabled && (
        <div className="mt-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddItem}
            className="h-7 text-xs"
          >
            <Plus className="w-3 h-3 mr-1" />
            Add Drug
          </Button>
        </div>
      )}

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteConfirmIndex !== null}
        onOpenChange={open => !open && setDeleteConfirmIndex(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Drug?</DialogTitle>
            <DialogDescription>
              This action cannot be undone. This will permanently delete this drug entry and all its
              data.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmIndex(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deleteConfirmIndex !== null && handleDeleteItem(deleteConfirmIndex)}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// =============================================================================
// PREVIEW COMPONENT
// =============================================================================

/** Item shape for preview (defined locally, schema provides field definitions at runtime) */
interface DrugPreviewItem {
  code?: string;
  name?: string;
}

/**
 * DrugInputFieldPreview - Pill-based preview component for j_code array field
 *
 * Displays drugs as pills with format: name · code. Overflow past `maxItems` is
 * reachable on hover — see {@link PillPreviewList}.
 */
export const DrugInputFieldPreview: React.FC<PreviewProps> = ({ data, maxItems = 2 }) => {
  const items = (Array.isArray(data) ? data : []) as DrugPreviewItem[];

  const pills: PreviewPill[] = items.flatMap((item, i) => {
    const parts = [item.name, item.code].filter(Boolean);
    if (parts.length === 0) return [];
    return [{ key: item.code || `drug-${i}`, text: parts.join(' · ') }];
  });

  return <PillPreviewList pills={pills} maxItems={maxItems} />;
};
